export interface NavLink {
  label: string;
  short: string;
  /** Internal route — omitted for outbound hub links. */
  to?: string;
  /** Outbound destination for hub links. */
  href?: string;
  external?: boolean;
}

/**
 * The primary hub navigation.
 * Chef Eddie G and Locavore PODS are outbound — this site introduces those
 * worlds and then sends visitors to their own dedicated homes. Culinary
 * Journeys, Hire Eddie and the rest live here with the fuller detail.
 */
export const navLinks: NavLink[] = [
  { label: "Chef Eddie G", short: "Chef", href: "https://chefeddieg.org", external: true },
  { label: "Locavore PODS", short: "Locavore", href: "https://locavorepods.com", external: true },
  { label: "Culinary Journeys", short: "Journeys", to: "/culinary-journeys" },
  { label: "Bring Eddie", short: "Bring Eddie", to: "/bring-eddie" },
  { label: "Journal", short: "Journal", to: "/journal" },
  { label: "Hire Eddie", short: "Hire", to: "/hire-eddie" },
  { label: "Submit a Locavore", short: "Submit", to: "/submit-a-locavore" },
];

export const externalLinks = {
  chefEddieG: "https://chefeddieg.org",
  locavorePods: "https://locavorepods.com",
};

export const footerLinks = [
  { label: "Culinary Journeys", to: "/culinary-journeys" },
  { label: "Bring Eddie", to: "/bring-eddie" },
  { label: "Invite Eddie", to: "/invite-eddie" },
  { label: "Hire Eddie", to: "/hire-eddie" },
  { label: "Submit a Locavore", to: "/submit-a-locavore" },
  { label: "Journal", to: "/journal" },
];

/** Official social links — placeholders until provided. */
export const socialLinks = [
  { label: "Instagram", icon: "ri-instagram-line", url: "#" },
  { label: "YouTube", icon: "ri-youtube-line", url: "#" },
  { label: "Facebook", icon: "ri-facebook-circle-line", url: "#" },
  { label: "LinkedIn", icon: "ri-linkedin-line", url: "#" },
  { label: "X", icon: "ri-twitter-x-line", url: "#" },
  { label: "TikTok", icon: "ri-tiktok-line", url: "#" },
];

/**
 * REAL supplied photography of Chef Eddie G — never replaced with AI people.
 * Used across every world, hero and personal moment.
 */
export const photos = {
  cruise:
    "https://static.readdy.ai/image/97b9d69681811246598e15b61e95ff01/aea4da664839f68e2dc171571ef5af01.png",
  // Dedicated hero image for the Culinary Journeys page — supplied by the client
  journeysHero:
    "https://storage.helloreaddy.io/project_files/1e7e4cf6-af0d-4f68-a1ae-f4eb7d8865a2/3a9fccd8-faf6-4189-b15a-4c735940ba19_compressed_image.webp",
  portrait:
    "https://static.readdy.ai/image/97b9d69681811246598e15b61e95ff01/537a5ccfbb7faa32ccd06e4d90377885.png",
  table:
    "https://static.readdy.ai/image/97b9d69681811246598e15b61e95ff01/e69760d6614b356836a6b3c2ca91a2bd.png",
  crew:
    "https://static.readdy.ai/image/97b9d69681811246598e15b61e95ff01/ccd8e13480afbdce020e64505659dc84.png",
  marina:
    "https://static.readdy.ai/image/97b9d69681811246598e15b61e95ff01/08b37b3f85b323a5e0d35857c8fe6dd4.png",
  // Chef Eddie G — the primary portrait, supplied by the client (hero banner)
  seated:
    "https://static.readdy.ai/image/97b9d69681811246598e15b61e95ff01/83155b2ea98a5ead5cc316c70711b7a5.png",
  diptych:
    "https://static.readdy.ai/image/97b9d69681811246598e15b61e95ff01/c672d2ec151c3b7559b4d56b55bcc4b6.jpeg",
  road:
    "https://static.readdy.ai/image/97b9d69681811246598e15b61e95ff01/37c90406a7b4e723a9ea457a3cdea7c2.png",
  // The couple — Chef Eddie G and his wife, supplied by the client
  couple:
    "https://static.readdy.ai/image/97b9d69681811246598e15b61e95ff01/c8917c7d29915a3f0922ac711d375854.jpeg",
  // Chef Eddie G and his wife — the formal portrait, supplied by the client
  coupleFormal:
    "https://static.readdy.ai/image/97b9d69681811246598e15b61e95ff01/44e2d1f528874ff31a9826fa10c2dfda.png",
  // Eddie & Kiki travelling together — supplied by the client
  coupleTravel:
    "https://static.readdy.ai/image/97b9d69681811246598e15b61e95ff01/7838798993746839793fddfce429463c.png",
  // Eddie at events, in the kitchen and on the market floor — client supplied
  eventChef:
    "https://storage.helloreaddy.io/project_files/1e7e4cf6-af0d-4f68-a1ae-f4eb7d8865a2/98b6db88-a195-4c89-8779-af5acc611bc4_compressed_WhatsApp-Image-2026-10-04-at-2.59.48-PM-2.webp",
  eventKitchen:
    "https://storage.helloreaddy.io/project_files/1e7e4cf6-af0d-4f68-a1ae-f4eb7d8865a2/a3cd8b25-60ba-45d3-a42d-aa47802b7fdc_compressed_WhatsApp-Image-2026-10-04-at-2.59.47-PM-4.webp",
  eventMarket:
    "https://storage.helloreaddy.io/project_files/1e7e4cf6-af0d-4f68-a1ae-f4eb7d8865a2/1945e510-1a7f-4cf4-838d-64ca11d7a18e_compressed_WhatsApp-Image-2026-10-04-at-2.59.46-PM-2.webp",
};

/**
 * The story of Chef Eddie G.
 * Reworked for a clean editorial read — every claim comes from the client.
 */
export const chefBio = {
  long: "Celebrity Chef Eddie G is a dynamic culinary professional known for bold flavors, creative cuisine, and memorable dining experiences. His career spans restaurants, television, and high-profile culinary events — including appearances on the Food Network — and he has become a sought-after chef and host for NFL VIP events, celebrity gatherings, and exclusive private experiences. What sets him apart is a dedication to culinary excellence, to entertainment, and to the simple act of bringing people together.",
  short:
    "Celebrity Chef Eddie G is a dynamic culinary professional known for bold flavors, creative cuisine, and memorable dining experiences — with a career spanning restaurants, television, and high-profile culinary events.",
};

/** Supporting atmospheric photography (food, place, travel — no invented people). */
export const images = {
  heroWide: photos.cruise,
  chefPortrait: photos.portrait,
  chefTable: photos.table,
  kitchenCrew: photos.crew,
  marina: photos.marina,
  couple: photos.couple,
  locavoreMark: "https://locavorepods.com/coming-soon.png",
  locavoreMarket:
    "https://readdy.ai/api/search-image?query=farmers%20market%20table%20overflowing%20with%20heirloom%20vegetables%20and%20fresh%20produce%2C%20documentary%20editorial%20photography%2C%20warm%20natural%20morning%20light%2C%20earthy%20muted%20palette%2C%2035mm%20film%20grain%2C%20shallow%20depth%20of%20field&width=1200&height=1500&seq=locavore-market-floor&orientation=portrait",
  locavoreFarm:
    "https://readdy.ai/api/search-image?query=rows%20of%20vegetables%20on%20a%20local%20organic%20farm%20at%20dawn%20with%20soft%20mist%20and%20empty%20fields%2C%20documentary%20editorial%20photography%2C%20warm%20natural%20light%2C%20earthy%20muted%20palette%2C%2035mm%20film%20grain&width=1200&height=800&seq=locavore-farm-dawn&orientation=landscape",
  foodPlating:
    "https://readdy.ai/api/search-image?query=rustic%20plated%20dish%20with%20fresh%20herbs%20and%20roasted%20vegetables%20on%20a%20dark%20wooden%20table%2C%20editorial%20food%20photography%2C%20warm%20moody%20light%2C%20muted%20earthy%20palette%2C%2035mm%20film%20grain&width=1400&height=900&seq=food-plating-editorial&orientation=landscape",
  marketLane:
    "https://readdy.ai/api/search-image?query=narrow%20old-town%20market%20lane%20with%20produce%20crates%20and%20hanging%20lanterns%20at%20dusk%2C%20documentary%20editorial%20photography%2C%20warm%20muted%20palette%2C%2035mm%20film%20grain&width=1200&height=1500&seq=market-lane-editorial&orientation=portrait",
  travelRoad:
    "https://readdy.ai/api/search-image?query=winding%20coastal%20road%20at%20golden%20hour%20with%20empty%20horizon%20and%20sea%20mist%2C%20cinematic%20editorial%20travel%20photography%2C%20warm%20muted%20palette%2C%2035mm%20film%20grain&width=1400&height=900&seq=travel-coastal-road&orientation=landscape",
  cellar:
    "https://readdy.ai/api/search-image?query=old%20stone%20cellar%20with%20rows%20of%20oak%20barrels%20and%20a%20single%20shaft%20of%20warm%20light%2C%20documentary%20editorial%20photography%2C%20muted%20earthy%20palette%2C%2035mm%20film%20grain&width=1400&height=900&seq=cellar-barrels&orientation=landscape",
};

export const cookingTips = [
  {
    no: "01",
    title: "Salt in layers, not at the end",
    note: "Season each stage of the cook so flavour builds from the inside out.",
  },
  {
    no: "02",
    title: "Let the pan get properly hot",
    note: "Patience before the sear is what creates colour, crust and depth.",
  },
  {
    no: "03",
    title: "Shop the season, cook the season",
    note: "The best ingredient list is whatever the market is proud of today.",
  },
  {
    no: "04",
    title: "Finish with acid",
    note: "A squeeze of citrus or a splash of vinegar wakes a whole dish up.",
  },
];

/** The four roles Eddie moves between — labels, not invented facts. */
export const roles = [
  { title: "Chef", note: "Food is the language." },
  { title: "Traveler", note: "The world is the menu." },
  { title: "Storyteller", note: "Every table has one." },
  { title: "Locavore", note: "Rooted in place." },
];

export interface World {
  no: string;
  key: string;
  title: string;
  kicker: string;
  line: string;
  cta: string;
  image: string;
  /** Internal route — omitted for outbound hub links. */
  to?: string;
  /** Outbound destination for hub links. */
  href?: string;
  external?: boolean;
}

export const worlds: World[] = [
  {
    no: "01",
    key: "chef",
    title: "Chef Eddie G",
    kicker: "World 01",
    line: "The chef, the story, the food — the person behind the journey, on his own dedicated site.",
    cta: "Visit chefeddieg.org",
    href: "https://chefeddieg.org",
    external: true,
    image: photos.portrait,
  },
  {
    no: "02",
    key: "locavore",
    title: "Locavore PODS",
    kicker: "World 02",
    line: "The local food movement and the people, places and experiences behind it.",
    cta: "Visit locavorepods.com",
    href: "https://locavorepods.com",
    external: true,
    image: photos.crew,
  },
  {
    no: "03",
    key: "journeys",
    title: "Culinary Journeys",
    kicker: "World 03",
    line: "Follow Eddie's travels, events, culinary adventures and upcoming destinations.",
    cta: "See the Journeys",
    to: "/culinary-journeys",
    image: photos.cruise,
  },
  {
    no: "04",
    key: "bring",
    title: "Bring Eddie To You",
    kicker: "World 04",
    line: "Invite, hire or partner with Chef Eddie G for your next event, experience or collaboration.",
    cta: "Connect with Eddie",
    to: "/bring-eddie",
    image: photos.marina,
  },
];